// Token-based inline styles shared by AssignmentImport and its DraftRow rows.
// Extracted to keep AssignmentImport.js under the 500-line limit.
import tokens from "../theme/tokens";

const { color, font, space, radius } = tokens;

const pad = `${space.sm}px ${space.md}px`;

const st = {
  header: { display: "flex", flexDirection: "column", gap: space.xs, marginBottom: space.md },
  headerTitle: { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary },
  headerNote: { fontSize: font.size.label, color: color.text.secondary },
  uploadRow: { display: "flex", flexWrap: "wrap", gap: space.sm, alignItems: "center" },
  sheetRow: {
    display: "flex", flexWrap: "wrap", gap: space.sm, alignItems: "center",
    justifyContent: "space-between", marginTop: space.md, paddingTop: space.md,
    borderTop: `1px solid ${color.border.default}`,
  },
  fileLabel: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, minWidth: 160 },
  // ⚠️ `minHeight` IS THE TARGET SIZE, NOT A LOOK. Measured in Chrome on the
  // deployed /daily-list, 2026-08-20: **1047 x 22** — wide enough, 2px short of
  // the 24px floor in WCAG 2.2 SC 2.5.8. A file input is pressable across its
  // WHOLE box (clicking the "no file chosen" text opens the chooser too), so the
  // element's own rect is the target and 2px of min-height is the entire fix.
  // It costs the row nothing: `uploadRow` is `align-items: center` and already
  // ~40px tall because of the "Import Excel" <Button> beside it, so growing a
  // 22px item to 24px moves nothing. Padding would NOT have been equivalent —
  // it inflates the native "Choose File" chrome inside the control.
  fileInput: { fontFamily: font.family, fontSize: font.size.body, flex: 1, minWidth: 200, minHeight: 24 },
  errorBanner: {
    background: color.surface.error,
    border: `1px solid ${color.accent.error}`,
    borderRadius: radius.sm,
    padding: pad,
    color: color.accent.error,
    fontSize: font.size.body,
    marginBottom: space.sm,
  },
  spinnerRow: { display: "flex", alignItems: "center", gap: space.sm, padding: `${space.sm}px 0` },
  spinner: { fontSize: 22, color: color.accent.primary, animation: "spin 1s linear infinite" },
  spinnerLabel: { fontSize: font.size.body, color: color.text.secondary },
  aicWarning: {
    background: "#fffbeb",
    border: `1px solid #f59e0b`,
    borderRadius: radius.sm,
    padding: pad,
    color: "#92400e",
    fontSize: font.size.body,
    marginBottom: space.sm,
  },
  flagSummary: {
    fontSize: font.size.label,
    color: color.text.secondary,
    marginBottom: space.sm,
  },
  tableWrap: {
    overflowX: "auto",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    marginBottom: space.md,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: font.family,
  },
  headRow: {
    background: color.surface.raised,
    borderBottom: `1px solid ${color.border.default}`,
  },
  th: {
    padding: `${space.xs}px ${space.sm}px`,
    textAlign: "left",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  td: {
    padding: `${space.xs}px ${space.sm}px`,
    borderBottom: `1px solid ${color.border.default}`,
    verticalAlign: "top",
  },
  flaggedRow: { background: "#fffbeb" },
  cell: {
    fontFamily: font.family, fontSize: font.size.body,
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    padding: `${space.xs}px ${space.sm}px`, background: color.surface.card,
    color: color.text.primary, width: "100%", boxSizing: "border-box",
  },
  attendingCell: { display: "flex", alignItems: "center", gap: space.xs, flexWrap: "wrap" },
  flagBadge: {
    display: "inline-block", background: "#fef9c3", border: "1px solid #fbbf24",
    borderRadius: radius.sm, padding: "1px 6px", fontSize: font.size.label,
    color: "#92400e", marginLeft: space.xs, whiteSpace: "nowrap",
  },
  callOrderBadge: {
    display: "inline-block", background: color.surface.raised,
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    padding: "1px 6px", fontSize: font.size.label, color: color.text.secondary,
    marginLeft: space.xs, whiteSpace: "nowrap",
  },
  aicBadge: {
    display: "inline-block", background: color.accent.primary, color: "#fff",
    borderRadius: radius.sm, padding: "1px 7px", fontSize: font.size.label,
    fontWeight: font.weight.bold, marginLeft: space.xs, whiteSpace: "nowrap",
  },
  actions: { display: "flex", gap: space.sm, justifyContent: "flex-end", flexWrap: "wrap", marginTop: space.sm },

  // ── Recent imports list (idle phase, below the pickers) ──
  recentWrap: {
    marginTop: space.md,
    paddingTop: space.md,
    borderTop: `1px solid ${color.border.default}`,
  },
  recentTitle: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: space.sm,
  },
  recentList: { display: "flex", flexDirection: "column", gap: space.xs },
  recentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
    width: "100%",
    textAlign: "left",
    padding: `${space.sm}px ${space.md}px`,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    cursor: "pointer",
    fontFamily: font.family,
    transition: "background 120ms ease, border-color 120ms ease",
  },
  recentItemMain: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  recentName: {
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  recentMeta: { fontSize: font.size.label, color: color.text.secondary },
  recentRight: { display: "flex", alignItems: "center", gap: space.xs, flexShrink: 0 },
  recentBadge: {
    display: "inline-block",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "1px 7px",
    fontSize: font.size.label,
    color: color.text.secondary,
    whiteSpace: "nowrap",
  },
  recentTime: { fontSize: font.size.label, color: color.text.secondary, whiteSpace: "nowrap" },
  recentReimport: { fontSize: font.size.label, color: color.accent.primary, fontWeight: font.weight.bold, whiteSpace: "nowrap" },
};

export default st;
